import { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, Loader2, Save, Pencil, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CODE_COLORS = {
    P: 'bg-orange-100 text-orange-800', T: 'bg-green-100 text-green-800', C: 'bg-red-100 text-red-800',
    R: 'bg-emerald-100 text-emerald-800', W: 'bg-lime-100 text-lime-800', H: 'bg-teal-100 text-teal-800',
    L: 'bg-blue-100 text-blue-800', S: 'bg-purple-100 text-purple-800', SS: 'bg-violet-100 text-violet-800',
    B: 'bg-rose-100 text-rose-800', A: 'bg-cyan-100 text-cyan-800', E: 'bg-blue-200 text-blue-900',
};

export const BankCategoriesDialog = ({ open, onOpenChange }) => {
    const { getAuthHeaders } = useAuth();
    const [data, setData] = useState(null);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [editValue, setEditValue] = useState('');

    const saveBankNote = async (code) => {
        const bank_notes = Object.fromEntries(data.items.filter((i) => i.is_custom).map((i) => [i.code, i.hint]));
        bank_notes[code] = editValue.trim();
        try {
            const r = await axios.put(`${API}/api/admin/bank-categories/notes`, { bank_notes }, { headers: getAuthHeaders() });
            setData((d) => ({
                ...d,
                updated_at: r.data.updated_at,
                updated_by: r.data.updated_by,
                items: d.items.map((i) => (i.code === code ? { ...i, hint: editValue.trim(), is_custom: true } : i)),
            }));
            setEditingCode(null);
            toast.success(`Nota de ${code} guardada`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar la nota');
        }
    };

    useEffect(() => {
        if (!open) return;
        axios.get(`${API}/api/admin/bank-categories`, { headers: getAuthHeaders() })
            .then((r) => { setData(r.data); setNotes(r.data.notes || ''); })
            .catch(() => toast.error('No se pudieron cargar las categorías'));
    }, [open, getAuthHeaders]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await axios.put(`${API}/api/admin/bank-categories/notes`, { notes }, { headers: getAuthHeaders() });
            setData((d) => ({ ...d, notes, updated_at: r.data.updated_at, updated_by: r.data.updated_by }));
            toast.success('Notas guardadas');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full max-h-[90vh] overflow-y-auto z-[70] p-4 sm:p-6" data-testid="bank-categories-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-sky-600" /> Categorías por banco</DialogTitle>
                    <DialogDescription>Referencia de la letra que se asigna a cada banco al subir facturas.</DialogDescription>
                </DialogHeader>
                {!data ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
                ) : (
                    <>
                        <table className="w-full text-sm" data-testid="bank-categories-table">
                            <thead><tr className="text-left text-slate-500 border-b"><th className="py-1.5 pr-2">Cat.</th><th className="py-1.5 pr-2">Banco</th><th className="py-1.5">Nota <span className="font-normal text-slate-400">(clic para editar)</span></th></tr></thead>
                            <tbody>
                                {data.items.map((it) => (
                                    <tr key={it.code} className="border-b border-slate-100 group" data-testid={`bank-category-row-${it.code}`}>
                                        <td className="py-1.5 pr-2 align-top"><span className={`inline-block min-w-[2rem] text-center font-bold rounded px-1.5 py-0.5 text-xs ${CODE_COLORS[it.code] || 'bg-slate-100 text-slate-700'}`}>{it.code}</span></td>
                                        <td className="py-1.5 pr-2 font-medium text-slate-800 align-top">{it.bank}</td>
                                        <td className="py-1.5 text-xs text-slate-500">
                                            {editingCode === it.code ? (
                                                <span className="flex items-center gap-1">
                                                    <input
                                                        autoFocus
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') saveBankNote(it.code); if (e.key === 'Escape') setEditingCode(null); }}
                                                        maxLength={300}
                                                        className="flex-1 h-7 px-2 text-xs border border-sky-300 rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                                                        placeholder={`Nota para ${it.bank}`}
                                                        data-testid={`bank-note-input-${it.code}`}
                                                    />
                                                    <button type="button" onClick={() => saveBankNote(it.code)} className="p-1 rounded text-green-600 hover:bg-green-50" title="Guardar" data-testid={`bank-note-save-${it.code}`}><Check className="w-3.5 h-3.5" /></button>
                                                    <button type="button" onClick={() => setEditingCode(null)} className="p-1 rounded text-slate-400 hover:bg-slate-100" title="Cancelar"><X className="w-3.5 h-3.5" /></button>
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => { setEditingCode(it.code); setEditValue(it.hint || ''); }}
                                                    className="w-full text-left inline-flex items-start gap-1 hover:text-sky-700 rounded px-1 -mx-1 hover:bg-sky-50"
                                                    title="Editar nota"
                                                    data-testid={`bank-note-edit-${it.code}`}
                                                >
                                                    <span className={it.hint ? '' : 'italic text-slate-300'}>{it.hint || 'Añadir nota…'}</span>
                                                    <Pencil className="w-3 h-3 mt-0.5 shrink-0 text-slate-300 group-hover:text-sky-500" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="mt-3">
                            <label className="text-xs font-medium text-slate-600">Notas generales del administrador</label>
                            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Escribe aquí recordatorios: cuentas, excepciones, cambios de banco..." className="mt-1" data-testid="bank-categories-notes" />
                            <div className="flex items-center justify-between mt-2">
                                <span className="text-[11px] text-slate-400">{data.updated_at ? `Última edición: ${new Date(data.updated_at).toLocaleString('es-US')} · ${data.updated_by}` : 'Sin notas guardadas'}</span>
                                <Button size="sm" onClick={save} disabled={saving || notes === (data.notes || '')} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="bank-categories-save-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Guardar</>}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export const BankCategoriesSection = ({ onOpen }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="px-4 py-3 border-b border-slate-200 bg-sky-50">
            <button
                onClick={() => { onOpen?.(); setOpen(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 bg-sky-100/60 border border-sky-200 rounded-lg hover:bg-sky-100 transition-colors text-left"
                data-testid="open-bank-categories-btn"
            >
                <BookOpen className="w-4 h-4 text-sky-600 shrink-0" />
                <span className="text-sm font-medium text-sky-700 truncate">Categorías por banco</span>
            </button>
            <BankCategoriesDialog open={open} onOpenChange={setOpen} />
        </div>
    );
};

export default BankCategoriesSection;
